import { useEffect, useMemo, useRef, useState } from "react";
import { message, Spin, Input, Button, Tooltip } from "antd";
import * as signalR from "@microsoft/signalr";
import { serverFetch, apiUrl } from "@server";
import { fmtDateTimeFull } from "@extensions/utils";
import MessageDialogsItem from "./message-dialogs-item";
import MessageDialog from "./message-dialog";
import "./message-dialogs.css";

const SITE_CHAT_VID = 201;

const getHubUrl = () => {
  const base = (apiUrl || "").replace(/\/api\/?$/i, "") || apiUrl;
  return `${base}/hubs/chat`;
};

const MessageDialogs = () => {
  const [dialogs, setDialogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loadingDialogs, setLoadingDialogs] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedDialogId, setSelectedDialogId] = useState(null);
  const [editDialogId, setEditDialogId] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [hubConnectionState, setHubConnectionState] = useState(null);
  const messagesListRef = useRef(null);
  const hubConnectionRef = useRef(null);
  const selectedDialogIdRef = useRef(selectedDialogId);
  selectedDialogIdRef.current = selectedDialogId;

  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
  }, [messages]);

  const scrollMessagesToEnd = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  };

  const getDialogs = () => {
    setLoadingDialogs(true);
    serverFetch("dialog")
      .then((data) => {
        setLoadingDialogs(false);
        setDialogs(data);
        if (data.length > 0 && !selectedDialogId) {
          setSelectedDialogId(data[0].id);
        }
      })
      .catch((e) => {
        setLoadingDialogs(false);
        message.error(e.userMessage || "Ошибка загрузки диалогов");
      });
  };

  const getMessages = (dialogId) => {
    if (!dialogId) return;
    setLoadingMessages(true);
    serverFetch(`message/ByDialog/${dialogId}`)
      .then((data) => {
        setLoadingMessages(false);
        setMessages(data);
      })
      .catch((e) => {
        setLoadingMessages(false);
        message.error(e.userMessage || "Ошибка загрузки сообщений");
      });
  };

  const loadTokens = () => {
    serverFetch("token?internalOnly=false")
      .then((data) => setTokens(Array.isArray(data) ? data : []))
      .catch(() => setTokens([]));
  };

  useEffect(() => {
    getDialogs();
    loadTokens();
  }, []);

  useEffect(() => {
    if (selectedDialogId) {
      getMessages(selectedDialogId);
    } else {
      setMessages([]);
    }
  }, [selectedDialogId]);

  useEffect(() => {
    const dialog = dialogs.find((d) => d.id === selectedDialogId);
    const isSiteChat = dialog?.vid === SITE_CHAT_VID && dialog?.code;
    const tokenCode = dialog?.tokenId != null ? (tokens.find((t) => t.id === dialog.tokenId)?.code ?? null) : null;

    if (!isSiteChat || !tokenCode) {
      setHubConnectionState(null);
      if (hubConnectionRef.current) {
        hubConnectionRef.current.stop().catch(() => {});
        hubConnectionRef.current = null;
      }
      return;
    }

    const hubUrl = getHubUrl();
    let connection = hubConnectionRef.current;

    const startAndJoin = async () => {
      if (!connection) {
        const sessionId = typeof localStorage !== "undefined" ? localStorage.getItem("sessionId") : null;
        let fullHubUrl = hubUrl;
        if (sessionId) {
          const sep = hubUrl.includes("?") ? "&" : "?";
          fullHubUrl = `${hubUrl}${sep}SessionId=${encodeURIComponent(sessionId)}`;
        }
        connection = new signalR.HubConnectionBuilder()
          .withUrl(fullHubUrl, { withCredentials: false })
          .withAutomaticReconnect()
          .build();
        hubConnectionRef.current = connection;

        connection.on("ReceiveMessage", (msg) => {
          const currentId = selectedDialogIdRef.current;
          if (msg?.dialogId !== currentId) return;
          const normalized = {
            id: msg.id,
            body: msg.body,
            direction: msg.direction,
            createdOn: msg.createdOn,
            pageName: msg.pageName,
            url: msg.url
          };
          setMessages((prev) => [...prev, normalized]);
          setTimeout(scrollMessagesToEnd, 0);
        });
        connection.onclose((e) => {
          setHubConnectionState(signalR.HubConnectionState.Disconnected);
          if (e) message.error("Чат отключён");
        });
      }

      setHubConnectionState(connection.state);
      if (connection.state !== signalR.HubConnectionState.Connected) {
        await connection.start();
        setHubConnectionState(connection.state);
      }
      await connection.invoke("Join", tokenCode, dialog.code);
      setHubConnectionState(connection.state);
    };

    startAndJoin().catch((err) => {
      setHubConnectionState(signalR.HubConnectionState.Disconnected);
      message.error(err?.message || "Ошибка подключения к чату");
    });

    const tick = () => {
      if (hubConnectionRef.current) setHubConnectionState(hubConnectionRef.current.state);
    };
    const interval = setInterval(tick, 500);

    return () => {
      clearInterval(interval);
      setHubConnectionState(null);
      if (hubConnectionRef.current) {
        hubConnectionRef.current.stop().catch(() => {});
        hubConnectionRef.current = null;
      }
    };
  }, [selectedDialogId, dialogs, tokens]);

  useEffect(() => {
    if (!loadingMessages && sortedMessages.length > 0) {
      scrollMessagesToEnd();
    }
  }, [loadingMessages, sortedMessages.length]);

  const handleDialogClick = (dialogId) => {
    setSelectedDialogId(dialogId);
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) {
      message.warning("Введите текст сообщения");
      return;
    }
    const selectedDialog = dialogs.find(d => d.id === selectedDialogId);
    if (!selectedDialog) {
      message.error("Не выбран диалог");
      return;
    }
    const hasPhone = selectedDialog.phone != null && String(selectedDialog.phone).trim() !== "";
    const hasCode = selectedDialog.code != null && String(selectedDialog.code).trim() !== "";
    if (!hasPhone && !hasCode) {
      message.error("Заполните номер телефона или код диалога");
      return;
    }

    const textToSend = messageText.trim();
    const isSiteChat = selectedDialog.vid === SITE_CHAT_VID;
    setSending(true);
    if (!isSiteChat) {
      setMessages((prev) => [
        ...prev,
        { id: "temp-" + Date.now(), body: textToSend, direction: -1, createdOn: new Date().toISOString() }
      ]);
    }
    setMessageText("");
    serverFetch("message/MessageSend", {
      method: "POST",
      bodyData: {
        phone: selectedDialog.phone,
        text: textToSend,
        dialogId: selectedDialogId
      }
    })
      .then(() => {
        message.success("Сообщение отправлено");
        if (!isSiteChat) {
          setTimeout(scrollMessagesToEnd, 0);
          getMessages(selectedDialogId);
        }
        getDialogs();
      })
      .catch((e) => {
        message.error(e.userMessage || "Ошибка при отправке сообщения");
      })
      .finally(() => {
        setSending(false);
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="message-dialogs-container">
      <div className="dialogs-column">
        <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold' }}>
          Диалоги
        </div>
        {loadingDialogs ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : dialogs.length > 0 ? (
          <div style={{ overflowY: 'auto', height: 'calc(100vh - 200px)' }}>
            {dialogs.map((dialog) => (
              <MessageDialogsItem
                key={dialog.id}
                dialog={dialog}
                selected={selectedDialogId === dialog.id}
                onClick={() => handleDialogClick(dialog.id)}
                onEdit={(dialogId) => setEditDialogId(dialogId)}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            Нет диалогов
          </div>
        )}
      </div>

      <div className="messages-column">
        <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Сообщения
          {selectedDialogId && dialogs.find((d) => d.id === selectedDialogId)?.vid === SITE_CHAT_VID && (
            <span
              title={
                hubConnectionState === signalR.HubConnectionState.Connected
                  ? "Чат подключён"
                  : hubConnectionState === signalR.HubConnectionState.Connecting ||
                      hubConnectionState === signalR.HubConnectionState.Reconnecting ||
                      hubConnectionState === null
                    ? "Подключение..."
                    : "Чат отключён"
              }
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor:
                  hubConnectionState === signalR.HubConnectionState.Connected
                    ? "#52c41a"
                    : hubConnectionState === signalR.HubConnectionState.Connecting ||
                        hubConnectionState === signalR.HubConnectionState.Reconnecting ||
                        hubConnectionState === null
                      ? "#faad14"
                      : "#ff4d4f",
                flexShrink: 0
              }}
            />
          )}
        </div>
        <div ref={messagesListRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {loadingMessages ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin />
            </div>
          ) : sortedMessages.length > 0 ? (
            <div style={{ padding: '16px', flex: 1 }}>
              {sortedMessages.map((msg) => {
                const bubble = (
                  <div
                    style={{
                      maxWidth: '70%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: msg.direction === 1 ? '#1890ff' : '#f0f0f0',
                      color: msg.direction === 1 ? 'white' : '#333',
                    }}
                  >
                    <div style={{ fontSize: '13px', marginBottom: '4px', opacity: 0.8 }}>
                      {fmtDateTimeFull(msg.createdOn)}
                    </div>
                    {msg.pageName != null && String(msg.pageName).trim() !== "" && (
                      <div style={{ fontSize: '12px', color: msg.direction === 1 ? 'rgba(255,255,255,0.85)' : '#888', marginBottom: '4px' }}>
                        {msg.pageName}
                      </div>
                    )}
                    <div style={{ fontSize: '14px', wordBreak: 'break-word' }}>
                      {msg.body || ""}
                    </div>
                  </div>
                );
                return (
                  <div
                    key={msg.id}
                    style={{
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: msg.direction === 1 ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {msg.url != null && String(msg.url).trim() !== "" ? (
                      <Tooltip title={msg.url}>{bubble}</Tooltip>
                    ) : (
                      bubble
                    )}
                  </div>
                );
              })}
            </div>
          ) : selectedDialogId ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Нет сообщений в этом диалоге
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Выберите диалог для просмотра сообщений
            </div>
          )}
        </div>
        {selectedDialogId && (
          <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
            <Input.TextArea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Введите сообщение..."
              rows={2}
              style={{ flex: 1 }}
              disabled={sending}
            />
            <Button
              type="primary"
              onClick={handleSendMessage}
              loading={sending}
              disabled={!messageText.trim() || sending}
            >
              Отправить
            </Button>
          </div>
        )}
      </div>
      {editDialogId != null && (
        <MessageDialog
          id={editDialogId}
          onClose={() => setEditDialogId(null)}
          onSuccess={getDialogs}
        />
      )}
    </div>
  );
};

export default MessageDialogs;

